import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { ArrowUpRight, Layers, Sparkles, Heart } from "lucide-react";

const HERO = "https://images.pexels.com/photos/16217479/pexels-photo-16217479.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const MODEL2 = "https://images.pexels.com/photos/28893442/pexels-photo-28893442.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SNEAKER = "https://images.unsplash.com/photo-1716347685367-1eb5de72eb65?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";

export default function Landing() {
  return (
    <div className="bg-bg0 min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7 ep-fade-up">
            <div className="overline mb-6 text-volt" data-testid="hero-overline">
              [ AI · Powered Styling Engine ]
            </div>
            <h1 className="font-display uppercase text-6xl sm:text-7xl lg:text-8xl font-bold tracking-tighter leading-[0.88]">
              Dress
              <br />
              <span className="text-volt">Different.</span>
            </h1>
            <p className="text-base md:text-lg text-epsub max-w-lg mt-8 leading-relaxed">
              Upload your fits. Eternal reads every piece — colour, cut, energy — then
              engineers head-to-toe looks with a stylist's eye. Streetwear-luxe, on demand.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-10">
              <Link
                to="/register"
                data-testid="hero-cta-primary"
                className="ep-btn group inline-flex items-center gap-2 bg-volt text-black px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-volt-hover"
              >
                Build Your Closet
                <ArrowUpRight size={17} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/login"
                data-testid="hero-cta-secondary"
                className="ep-btn inline-flex items-center gap-2 px-8 py-4 text-sm font-bold uppercase tracking-widest border border-white/25 hover:bg-white hover:text-black"
              >
                Sign In
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 ep-fade-up" style={{ animationDelay: "0.12s" }}>
            <div className="relative">
              <img src={HERO} alt="Streetwear model" className="w-full h-[440px] md:h-[580px] object-cover border border-epborder" />
              <div className="absolute -bottom-4 -left-4 bg-volt text-black px-5 py-3 volt-glow">
                <span className="font-mono-accent text-xs uppercase tracking-[0.2em] font-bold">Eternal · SS26</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE STRIP */}
      <div className="border-y border-epborder overflow-hidden py-5">
        <div className="flex items-center gap-8 whitespace-nowrap font-display uppercase text-2xl tracking-tight text-epmuted">
          {Array(8).fill(0).map((_, i) => (
            <span key={i} className="flex items-center gap-8">
              Coordinate <span className="text-volt">◆</span> Pair <span className="text-volt">◆</span> Flex <span className="text-volt">◆</span>
            </span>
          ))}
        </div>
      </div>

      {/* TRENDING BRANDS */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 pt-24 pb-8">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline mb-3 text-volt">Trending Now</div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl font-bold tracking-tighter leading-none">
              Icons on rotation.
            </h2>
          </div>
          <span className="hidden md:block overline">Grails · Heat · Staples</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="trending-grid">
          {[
            { url: "https://images.unsplash.com/photo-1552346154-21d32810aba3?crop=entropy&cs=srgb&fm=jpg&q=85&w=700", brand: "Air Jordan 1", tag: "Sneakers" },
            { url: "https://images.unsplash.com/photo-1627349864724-645aea4fbda0?crop=entropy&cs=srgb&fm=jpg&q=85&w=700", brand: "Denim Layer", tag: "Outerwear" },
            { url: "https://images.unsplash.com/photo-1576965316555-88f217994219?crop=entropy&cs=srgb&fm=jpg&q=85&w=700", brand: "Off-White Tee", tag: "Tops" },
            { url: "https://images.unsplash.com/photo-1575202332411-b01fe9ace7a8?crop=entropy&cs=srgb&fm=jpg&q=85&w=700", brand: "Leather Bag", tag: "Accessory" },
          ].map((p, i) => (
            <div key={i} className="relative overflow-hidden border border-epborder group aspect-[3/4]" data-testid={`trending-${i}`}>
              <img src={p.url} alt={p.brand} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="overline text-volt">{p.tag}</span>
                <div className="font-display uppercase text-xl font-semibold tracking-tight mt-1">{p.brand}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-epborder border border-epborder">
          {[
            { icon: Layers, t: "Catalogue", d: "Upload each piece. AI tags category, colour and vibe on sight — no manual tagging." },
            { icon: Sparkles, t: "Pair", d: "The stylist engine drops full looks with coordination scores and the reasoning behind each." },
            { icon: Heart, t: "Lookbook", d: "Save the fits that hit. Filter your saved looks by occasion in one tap." },
          ].map((f, i) => (
            <div key={i} className="bg-bg0 p-10 group hover:bg-epcard transition-colors" data-testid={`feature-${i}`}>
              <f.icon size={26} strokeWidth={1.5} className="text-volt" />
              <div className="overline mt-8 mb-3 text-volt">0{i + 1}</div>
              <h3 className="font-display uppercase text-3xl font-semibold tracking-tight">{f.t}</h3>
              <p className="text-epsub mt-4 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SHOWCASE */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch">
          <div className="md:col-span-4 relative overflow-hidden border border-epborder group">
            <img src={SNEAKER} alt="Sneakers" className="w-full h-full min-h-[300px] object-cover group-hover:scale-105 transition-transform duration-300" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <span className="absolute bottom-5 left-5 overline text-white">Sneaker Culture</span>
          </div>
          <div className="md:col-span-4 relative overflow-hidden border border-epborder group">
            <img src={MODEL2} alt="Streetwear" className="w-full h-full min-h-[300px] object-cover group-hover:scale-105 transition-transform duration-300" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <span className="absolute bottom-5 left-5 overline text-white">Downtown Fits</span>
          </div>
          <div className="md:col-span-4 bg-volt text-black p-10 flex flex-col justify-between">
            <div className="font-mono-accent text-xs uppercase tracking-[0.2em] font-bold">The Engine</div>
            <div>
              <h2 className="font-display uppercase text-4xl font-bold tracking-tighter leading-none mt-8">
                Both smart<br />& intentional.
              </h2>
              <p className="text-black/70 mt-5 leading-relaxed text-sm">
                Rule-based colour harmony fused with a vision AI that reads texture and silhouette. Fits that feel considered — never random.
              </p>
              <Link
                to="/register"
                data-testid="showcase-cta"
                className="ep-btn inline-flex items-center gap-2 mt-8 text-sm font-bold uppercase tracking-widest border-b-2 border-black pb-1 hover:gap-3"
              >
                Start free <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-epborder">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-10 flex items-center justify-between">
          <span className="font-display uppercase text-lg tracking-tight">Eternal<span className="text-volt">/</span>Pairing</span>
          <span className="overline">© 2026 · Engineered by AI</span>
        </div>
      </footer>
    </div>
  );
}
